import { useEffect, useState } from 'react';
import { Download, Info, Users, UserPlus, CheckCircle2 } from 'lucide-react';
import { getEvolution, EvolutionResp, EvolutionRow } from '../api';
import { Skeleton } from '../../../components/Skeleton';
import { readCache, writeCache } from '../../../services/localCache';
import EmptyState from '../../../components/EmptyState';
import { colorClase } from '../../../utils/classColor';

function monthLabel(ym: string) {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
}

export default function EvolutionReport() {
  const [resp, setResp] = useState<EvolutionResp | null>(null);
  const [month, setMonth] = useState<string>('');
  const [loading, setLoading] = useState(true);

  // Carga inicial: pide el mes más reciente que el backend conozca.
  // Pinta al instante lo último conocido del mes (caché local) y refresca por detrás.
  useEffect(() => {
    const m = month || new Date().toISOString().slice(0, 7);
    const cached = readCache<EvolutionResp>(`rep-evolution:${m}`);
    if (cached) { setResp(cached); setLoading(false); } else setLoading(true);
    getEvolution(m)
      .then((r) => { setResp(r); writeCache(`rep-evolution:${m}`, r); if (!month) setMonth(r.months[0] || m); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [month]);

  function exportCsv() {
    if (!resp) return;
    const lines = ['Clase,Profesor,Estilo,Inscritos,Nuevos,Asistencias,Pagos Mes,Bimestre,Trimestre,Otro'];
    for (const r of resp.data) {
      lines.push([
        `"${r.name.replace(/"/g, '')}"`, `"${(r.teacher || '').replace(/"/g, '')}"`, `"${(r.style || '').replace(/"/g, '')}"`,
        r.enrolled, r.nuevos, r.asistencias, r.pagos.mes, r.pagos.bimestre, r.pagos.trimestre, r.pagos.otro,
      ].join(','));
    }
    const t = resp.totals;
    lines.push(`TOTAL,,,${t.enrolled},${t.nuevos},${t.asistencias},${t.mes},${t.bimestre},${t.trimestre},${t.otro}`);
    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = `evolucion-clases-${resp.month}.csv`; a.click();
  }

  const totalPagos = (r: EvolutionRow) => r.pagos.mes + r.pagos.bimestre + r.pagos.trimestre + r.pagos.otro;

  return (
    <div>
      {/* Controles */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-content-3">Mes:</span>
          <select value={month} onChange={(e) => setMonth(e.target.value)}
            className="rounded-lg border border-line bg-surface px-3 py-1.5 text-sm capitalize text-content outline-none focus:border-brand-blue">
            {(resp?.months || []).map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
          </select>
        </div>
        {resp && resp.data.length > 0 && (
          <button onClick={exportCsv} className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-sm text-content-2 transition hover:bg-surface-2">
            <Download size={15} /> CSV
          </button>
        )}
      </div>

      {/* Aviso honesto de límites de datos */}
      <div className="mb-4 flex items-start gap-2 rounded-xl border border-line bg-surface-2/50 p-3 text-xs text-content-2">
        <Info size={15} className="mt-0.5 shrink-0 text-brand-blue" />
        <span>
          <b>Inscritos</b> = roster actual. <b>Nuevos</b> = alumnos cuyo 1er pago cae en el mes. <b>Asistencias</b> se
          llenan al pasar lista (sin histórico previo de Kydemy). Las <b>pruebas/cancelaciones</b> no se migraron, y un
          pago de mensualidad cuenta en cada clase del alumno (no está atado a una sola).
        </span>
      </div>

      {loading && !resp ? (
        <Skeleton className="h-64 w-full rounded-2xl" />
      ) : !resp || resp.data.length === 0 ? (
        <EmptyState icon={Users} title="Sin datos este mes" subtitle="No hay clases con actividad en el mes elegido." />
      ) : (
        <>
          {/* KPIs */}
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { label: 'Clases', value: resp.data.length, icon: Users },
              { label: 'Inscritos', value: resp.totals.enrolled, icon: Users },
              { label: 'Nuevos (1er pago)', value: resp.totals.nuevos, icon: UserPlus },
              { label: 'Pagos del mes', value: resp.totals.mes + resp.totals.bimestre + resp.totals.trimestre + resp.totals.otro, icon: CheckCircle2 },
            ].map((k) => (
              <div key={k.label} className="rounded-2xl border border-line bg-surface p-4 shadow-sm">
                <div className="text-xs text-content-3">{k.label}</div>
                <div className="font-display text-2xl font-bold text-content">{k.value}</div>
              </div>
            ))}
          </div>

          {/* Tabla (escritorio) */}
          <div className="hidden overflow-hidden rounded-2xl border border-line bg-surface shadow-sm sm:block">
            <table className="w-full text-sm">
              <thead className="border-b border-line bg-surface-2 text-content-3">
                <tr>
                  <th className="px-4 py-2.5 text-left font-medium">Clase</th>
                  <th className="px-3 py-2.5 text-right font-medium">Inscritos</th>
                  <th className="px-3 py-2.5 text-right font-medium">Nuevos</th>
                  <th className="px-3 py-2.5 text-right font-medium">Asist.</th>
                  <th className="px-3 py-2.5 text-right font-medium">Mes</th>
                  <th className="px-3 py-2.5 text-right font-medium">Bim.</th>
                  <th className="px-3 py-2.5 text-right font-medium">Trim.</th>
                  <th className="px-3 py-2.5 text-right font-medium">Otro</th>
                </tr>
              </thead>
              <tbody>
                {resp.data.map((r) => (
                  <tr key={r.class_id} className="border-t border-line even:bg-surface-2/40">
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: colorClase(r.color) }} />
                        <div className="min-w-0">
                          <div className="truncate font-medium text-content">{r.name}</div>
                          {r.teacher && <div className="truncate text-xs text-content-3">{r.teacher}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-content">{r.enrolled}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-content-2">{r.nuevos || '·'}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-content-3">{r.asistencias || '·'}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium text-content">{r.pagos.mes || '·'}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-content-2">{r.pagos.bimestre || '·'}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-content-2">{r.pagos.trimestre || '·'}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-content-2">{r.pagos.otro || '·'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-line bg-surface-2 font-semibold text-content">
                  <td className="px-4 py-2.5">Total</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{resp.totals.enrolled}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{resp.totals.nuevos}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{resp.totals.asistencias}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{resp.totals.mes}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{resp.totals.bimestre}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{resp.totals.trimestre}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{resp.totals.otro}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Tarjetas (móvil) */}
          <div className="space-y-2 sm:hidden">
            {resp.data.map((r) => (
              <div key={r.class_id} className="rounded-xl border border-line bg-surface p-3 shadow-sm">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: colorClase(r.color) }} />
                  <span className="truncate font-medium text-content">{r.name}</span>
                </div>
                <div className="mt-2 grid grid-cols-3 gap-2 text-center text-xs">
                  <div><div className="font-display text-lg font-bold text-content">{r.enrolled}</div><div className="text-content-3">inscritos</div></div>
                  <div><div className="font-display text-lg font-bold text-content">{r.nuevos}</div><div className="text-content-3">nuevos</div></div>
                  <div><div className="font-display text-lg font-bold text-content">{totalPagos(r)}</div><div className="text-content-3">pagos</div></div>
                </div>
                {totalPagos(r) > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5 text-[11px] text-content-3">
                    {r.pagos.mes > 0 && <span className="rounded bg-surface-2 px-1.5 py-0.5">Mes: {r.pagos.mes}</span>}
                    {r.pagos.bimestre > 0 && <span className="rounded bg-surface-2 px-1.5 py-0.5">Bim: {r.pagos.bimestre}</span>}
                    {r.pagos.trimestre > 0 && <span className="rounded bg-surface-2 px-1.5 py-0.5">Trim: {r.pagos.trimestre}</span>}
                    {r.pagos.otro > 0 && <span className="rounded bg-surface-2 px-1.5 py-0.5">Otro: {r.pagos.otro}</span>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
